class BottleQuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      productCard: 'product-card',
      option: '.bottle-option',
      label: '[data-bottle-quantity-label]',
    };
  }

  connectedCallback() {
    this.productCard = this.closest(this.selectors.productCard);
    this.options = Array.from(this.querySelectorAll(this.selectors.option));

    if (!this.productCard || this.options.length === 0) return;

    customElements.whenDefined('product-card')
      .then(() => this.init())
      .catch((err) => this.handleError('whenDefined failed', err));
  }

  init() {
    try {
      if (!this.productCard?.dataStore) {
        this.handleError('product-card dataStore not ready');
        return;
      }

      const initialQuantity = this.getInitialQuantity();
      this.setActive(initialQuantity);
      const currentCardQuantity = parseInt(this.productCard.data?.quantity?.value, 10);
      if (currentCardQuantity !== initialQuantity) {
        this.safeUpdateCard(initialQuantity);
      }

      this.options.forEach((option) => {
        option.addEventListener('click', (event) => this.onOptionClick(event, option));
      });

      this.productCard.dataStore.listen('quantity', (data) => {
        try {
          const quantity = parseInt(data.value, 10);
          if (!Number.isNaN(quantity)) this.setActive(quantity);
        } catch (err) {
          this.handleError('quantity listener failed', err);
        }
      });

      this.productCard.dataStore.listen('selected_variant', (data) => {
        try {
          this.updatePrices(data.value);
        } catch (err) {
          this.handleError('variant listener failed', err);
        }
      });

      this.updatePrices(this.productCard.data?.selected_variant?.value);
    } catch (err) {
      this.handleError('init failed', err);
    }
  }

  updatePrices(variant) {
    if (!variant) return;
    const unitPrice = variant.price;
    const unitCompare = variant.compare_at_price ?? 0;

    this.options.forEach((option) => {
      const qty = parseInt(option.dataset.quantity, 10);
      if (Number.isNaN(qty)) return;

      // Mirrors the Liquid in product-card-quantity-selector: the tier saving comes
      // from the metaobject in cents and only previews the automatic discount
      // Shopify applies at checkout. It is a flat amount, so it does not scale
      // with the variant price.
      const lineTotal = unitPrice * qty;
      const discount = Math.min(parseInt(option.dataset.discountAmount, 10) || 0, lineTotal);
      const total = lineTotal - discount;
      const compare = Math.max(unitCompare * qty, lineTotal);
      const hasCompare = compare > total;

      const totalEl = option.querySelector('[data-bottle-total]');
      const unitEl = option.querySelector('[data-bottle-unit]');
      const compareEl = option.querySelector('[data-bottle-compare]');
      const savingsEl = option.querySelector('[data-bottle-savings]');

      if (totalEl) totalEl.dataset.price = total;
      if (unitEl) unitEl.dataset.price = Math.floor(total / qty);

      if (compareEl) {
        compareEl.dataset.price = compare;
        const compareWrapper = compareEl.closest('.bottle-option__compare');
        if (compareWrapper) compareWrapper.classList.toggle('hidden', !hasCompare);
      }

      if (savingsEl) {
        // Shown as a percentage off the compare price, rounded the same way as the
        // Liquid that renders the initial value.
        savingsEl.textContent = `${compare > 0 ? Math.round(((compare - total) / compare) * 100) : 0}%`;
        const savingsWrapper = savingsEl.closest('.bottle-option__savings');
        if (savingsWrapper) savingsWrapper.classList.toggle('hidden', !hasCompare);
      }
    });
  }

  onOptionClick(event, option) {
    event.preventDefault();
    try {
      const quantity = parseInt(option.dataset.quantity, 10);
      if (Number.isNaN(quantity)) return;
      this.setActive(quantity);
      this.safeUpdateCard(quantity);
    } catch (err) {
      this.handleError('option click failed', err);
    }
  }

  safeUpdateCard(quantity) {
    try {
      this.productCard?.dataStore?.update('quantity', quantity);
    } catch (err) {
      this.handleError(`failed to set quantity ${quantity}`, err);
    }
  }

  getInitialQuantity() {
    const cardQuantity = parseInt(this.productCard?.data?.quantity?.value, 10);
    if (!Number.isNaN(cardQuantity) && this.hasOption(cardQuantity)) {
      return cardQuantity;
    }
    const defaultOption = this.options.find((o) => o.hasAttribute('data-default'));
    if (defaultOption) return parseInt(defaultOption.dataset.quantity, 10);
    return parseInt(this.options[0].dataset.quantity, 10);
  }

  hasOption(quantity) {
    return this.options.some((o) => parseInt(o.dataset.quantity, 10) === quantity);
  }

  setActive(quantity) {
    this.options.forEach((option) => {
      const optionQuantity = parseInt(option.dataset.quantity, 10);
      const isActive = optionQuantity === quantity;
      option.classList.toggle('active', isActive);
      option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    this.updateLabel(quantity);
  }

  updateLabel(quantity) {
    const labels = this.productCard?.querySelectorAll(this.selectors.label);
    if (!labels || labels.length === 0) return;
    const text = `${quantity} ${quantity === 1 ? 'Bottle' : 'Bottles'}`;
    labels.forEach((el) => { el.textContent = text; });
  }

  handleError(message, error) {
    console.error(`[bottle-quantity-selector] ${message}`, error ?? '');
    this.dispatchEvent(new CustomEvent('bottle-quantity-selector:error', {
      bubbles: true,
      detail: { message, error },
    }));
  }
}

if (!customElements.get('bottle-quantity-selector')) {
  customElements.define('bottle-quantity-selector', BottleQuantitySelector);
}
